import { useThemeSwitcher } from 'react-css-theme-switcher';
import { Button, Toolbar } from "@progress/kendo-react-buttons";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { Chart, ChartCategoryAxis, ChartCategoryAxisItem, ChartLegend, ChartSeries, ChartSeriesItem, ChartTitle, ChartTooltip, ChartValueAxis, ChartValueAxisItem } from "@progress/kendo-react-charts";
import { Grid, GridColumn as Column, GridCustomCellProps, GridNoRecords } from "@progress/kendo-react-grid";
import { Loader } from "@progress/kendo-react-indicators";
import { arrowRotateCwIcon } from "@progress/kendo-svg-icons";
import dayjs from "dayjs";
import PopupNavBar from "components/Layout/PopupNavBar";
import SimpleDateRangePicker from "components/SimpleDateRangePicker";
import { fetchApi } from "services/api";
import { Title } from "utils/title";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IDNameViewModel } from "TypeGen/id-name-view-model";
import { Link } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import OwnerAutoComplete from "views/AssetOwners/OwnerAutoComplete";

type VehicleViewModel = {
  VehicleID: number;
  VehicleNumber: string;
  DriverID1: number;
  Driver1Number: string;
  VehicleTypeName: string;
  HaulingCategoryName: string;
  CurrentScore: number;
};

type DriverScoreHistoryPoint = {
  Date: string;
  AverageScore: number;
  MedianScore: number;
};

type DashboardResponse = {
  ScoreHistory: DriverScoreHistoryPoint[];
  Vehicles: VehicleViewModel[];
  VehicleTypes: IDNameViewModel[];
  HaulingCategories: IDNameViewModel[];
};

const ScoreValueCell = (props: GridCustomCellProps) => {
  const value = props.dataItem[props.field as string];
  if (value === null || value === undefined) return <td {...props.tdProps} />;

  return (
    <td {...props.tdProps}>
      {Math.round(value).toLocaleString()}
    </td>
  );
};

const DriverScorecardDashboard = () => {
  const { status } = useThemeSwitcher();

  // Filters
  const [startDate, setStartDate] = useState(dayjs().tz("America/New_York").subtract(1, "week").startOf("isoWeek").toDate());
  const [endDate, setEndDate] = useState(dayjs().tz("America/New_York").endOf("day").toDate());
  const [vehicleTypeIds, setVehicleTypeIds] = useLocalStorage<number[]>("DriverScorecardDashboard_VehicleTypeIDs", []);
  const [haulingCategories, setHaulingCategories] = useLocalStorage<number[]>("DriverScorecardDashboard_HaulingCategories", []);
  const [ownerId, setOwnerId] = useLocalStorage<number>("DriverScorecardDashboard_OwnerID", 0);

  // Data
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    const payload = {
      FromDate: startDate,
      ToDate: dayjs(endDate).endOf("day").toDate(),
      VehicleTypeIDs: vehicleTypeIds,
      HaulingCategories: haulingCategories,
      OwnerID: ownerId > 0 ? ownerId : null
    };

    fetchApi("/api/Asset/DriverScorecardDashboard", payload, "POST")
      .then((response: DashboardResponse) => setData(response))
      .catch(() => {
        alert("Unable to load driver scorecard dashboard");
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, vehicleTypeIds, haulingCategories, ownerId]);

  const scoreHistoryCategories = useMemo(
    () => (data?.ScoreHistory ?? []).map(point => dayjs(point.Date).toDate()),
    [data?.ScoreHistory]
  );

  const scoreHistoryValues = useMemo(
    () => (data?.ScoreHistory ?? []).map(point => Number(point.AverageScore.toFixed(2))),
    [data?.ScoreHistory]
  );

  const medianScoreHistoryValues = useMemo(
    () => (data?.ScoreHistory ?? []).map(point => Number(point.MedianScore.toFixed(2))),
    [data?.ScoreHistory]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (status !== 'loaded') return <div>Loading...</div>;

  return (
    <div>
      <PopupNavBar />
      <Title string="Driver Scorecard Dashboard" />
      <nav className="navbar navbar-dark bg-dark">
        <a className="navbar-brand" href="/">Driver Scorecard Dashboard {loading && <Loader type="pulsing" className="p-0" />}</a>
        <ul className="nav navbar-nav navbar-right">
          <Button
            title="Refresh"
            icon="refresh"
            svgIcon={arrowRotateCwIcon}
            disabled={loading}
            onClick={refresh}
          />
        </ul>
      </nav>

      <div className="container-fluid">
        <Toolbar className="my-3">
          <SimpleDateRangePicker
            inputProps={{ disabled: loading }}
            value={{ start: startDate, end: endDate }}
            onChange={(range) => {
              setStartDate(range.start);
              setEndDate(range.end);
            }}
          />
          <MultiSelect
            data={data?.VehicleTypes || []}
            textField="Name"
            dataItemKey="ID"
            style={{ width: 200 }}
            placeholder="Vehicle Types"
            value={data?.VehicleTypes.filter(x => vehicleTypeIds.includes(x.ID))}
            onChange={(e) => setVehicleTypeIds(e.value.map((item: IDNameViewModel) => item.ID))}
            disabled={loading}
          />
          <MultiSelect
            data={data?.HaulingCategories || []}
            textField="Name"
            dataItemKey="ID"
            style={{ width: 200 }}
            placeholder="Hauling Categories"
            value={data?.HaulingCategories.filter(x => haulingCategories.includes(x.ID))}
            onChange={(e) => setHaulingCategories(e.value.map((item: IDNameViewModel) => item.ID))}
            disabled={loading}
          />
          <OwnerAutoComplete
            style={{ width: 260 }}
            placeholder="Owner"
            selectedOwnerID={ownerId}
            onSelectedOwner={(owner) => setOwnerId(owner?.OwnerID ?? 0)}
            disabled={loading}
          />
        </Toolbar>
        {data && <>
          {(data.ScoreHistory?.length ?? 0) > 0 && <div className="mb-3">
            <Chart transitions={false}>
              <ChartTitle text="Fleet Score Trend" />
              <ChartLegend position="bottom" orientation="horizontal" />
              <ChartCategoryAxis>
                <ChartCategoryAxisItem categories={scoreHistoryCategories} type="date" baseUnit="days" />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem narrowRange={true} />
              </ChartValueAxis>
              <ChartSeries>
                <ChartSeriesItem type="line" name="Average Score" data={scoreHistoryValues} markers={{ visible: true }} />
                <ChartSeriesItem type="line" name="Mean Score" data={medianScoreHistoryValues} markers={{ visible: true }} />
              </ChartSeries>
              <ChartTooltip shared={true} format="{0:n0}" />
            </Chart>
          </div>}

          <Grid
            sortable
            defaultSort={[{ field: "Rank", dir: "asc" }]}
            groupable
            autoProcessData
            data={data.Vehicles}
            dataItemKey="VehicleID"
            scrollable="none"
          >
            <GridNoRecords>
              No vehicles found matching the selected criteria.
            </GridNoRecords>
            <Column field="Rank" title="Rank" groupable={false} filter="numeric" width={80} />
            <Column field="Driver1Number" title="Driver" groupable={false} cells={{ data: (e) => <td {...e.tdProps}>
              <Link style={{ color: "#007bff" }} to={`/Assets/Driver/${e.dataItem.DriverID1}/Scorecard`}>{e.dataItem.Driver1Number}</Link>
            </td> }} />
            <Column field="VehicleNumber" title="Vehicle" groupable={false} cells={{ data: (e) => <td {...e.tdProps}>
              <Link style={{ color: "#007bff" }} to={`/Assets/Vehicle/${e.dataItem.VehicleID}`}>{e.dataItem.VehicleNumber}</Link>
            </td> }} />
            <Column field="VehicleTypeName" title="Vehicle Type" />
            <Column field="HaulingCategoryName" title="Hauling Category" />
            <Column field="CurrentScore" title="Score" cells={{ data: ScoreValueCell }} groupable={false} filter="numeric" />
          </Grid>
          <br />
          </>}
      </div>
    </div>
  );
};

export default DriverScorecardDashboard;
